import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import HomeIcon from '@mui/icons-material/Home'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { TestCreateSectionHeader } from '@/components/tests/TestCreateSectionHeader'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { testsApi, type TestPreviewRunRequestPayload } from '@/api/tests'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import { useAuthStore } from '@/stores/authStore'
import {
  ANSWER_FORMAT_MODES,
  PROMPT_SOURCE_TEXT,
  PROMPT_TRANSLATION_TEXT,
  type AnswerFormatMode,
  granularAnswerTypesFromModes,
  isSpeechAnswerType,
  modeHasCompatibleGranular,
  unionAnswerTypesForPromptPool,
} from '@/utils/testPromptAnswerMatrix'
import '@/styles/dashboard.css'

type PromptTypeKey = typeof PROMPT_SOURCE_TEXT | typeof PROMPT_TRANSLATION_TEXT

const SELF_PROMPT_OPTIONS: Array<{ id: PromptTypeKey }> = [
  { id: PROMPT_SOURCE_TEXT },
  { id: PROMPT_TRANSLATION_TEXT },
]

const SELF_SKILL_IDS = ['translation', 'reverse_translation'] as const

const LESSON_TEST_SECTION_CARD_SX: SxProps<Theme> = {
  scrollMarginTop: 16,
  p: 0,
}

const LESSON_TEST_SECTION_CARD_CONTENT_SX: SxProps<Theme> = {
  p: 2,
  '&:last-child': { pb: 2 },
}

const PREVIEW_DEBOUNCE_MS = 3_000

type PreviewState = {
  count_need: number
  count_found: number
  shortage: number
  card_ids: number[]
}

function SelfPreviewPanel(props: {
  preview: PreviewState | null
  isLoading: boolean
  selectionBlockHint: string | null
  canRunPreview: boolean
}) {
  const { preview, isLoading, selectionBlockHint, canRunPreview } = props
  const { t } = useTranslation()
  const pct = preview?.count_need ? Math.min(100, Math.round((preview.count_found / preview.count_need) * 100)) : 0
  const hasShortage = Boolean(preview && preview.shortage > 0)

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 } }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('tests.preview.panelTitle')}
      </Typography>

      {isLoading ? <LinearProgress sx={{ mb: 2 }} /> : <Box sx={{ height: 4, mb: 2 }} />}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
          <Typography
            variant="h5"
            sx={(theme) => ({ fontFamily: 'monospace', lineHeight: 1.1, color: theme.palette.primary.main })}
          >
            {preview?.count_need ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('tests.preview.wordsPlanned')}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
          <Typography variant="h5" sx={{ fontFamily: 'monospace', lineHeight: 1.1 }}>
            {preview ? Math.max(0, preview.shortage) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('tests.preview.shortage')}
          </Typography>
        </Paper>
      </Stack>

      {preview ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 99, mb: 0.75 }} />
          <Typography variant="caption" color="text.secondary">
            {preview.count_found}/{preview.count_need}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          {selectionBlockHint ??
            (canRunPreview
              ? t('tests.preview.emptyHint')
              : t('tests.createForm.previewNeedDisplayAndFormatHint', {
                  display: t('tests.createForm.sectionDisplay'),
                  answerFormat: t('tests.createForm.sectionAnswerFormat'),
                }))}
        </Typography>
      )}

      {hasShortage && preview ? (
        <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
          {t('tests.preview.summary', {
            found: preview.count_found,
            need: preview.count_need,
            shortage: preview.shortage,
          })}
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('tests.preview.cardIds')}
      </Typography>
      <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
        <List dense disablePadding>
          {(preview?.card_ids ?? []).length === 0 ? (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                primary="—"
              />
            </ListItem>
          ) : (
            (preview?.card_ids ?? []).map((cid) => (
              <ListItem key={cid} divider>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`#${cid}`} />
              </ListItem>
            ))
          )}
        </List>
      </Paper>
    </Paper>
  )
}

export function SelfTestCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useAuthStore((s) => s.user)

  const [enabledSkillTypes, setEnabledSkillTypes] = useState<string[]>(['translation'])
  const [enabledPromptTypes, setEnabledPromptTypes] = useState<PromptTypeKey[]>([PROMPT_SOURCE_TEXT])
  const [enabledAnswerFormatModes, setEnabledAnswerFormatModes] = useState<AnswerFormatMode[]>([])
  const [allowHints, setAllowHints] = useState(false)
  const [allowTranscription, setAllowTranscription] = useState(false)
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(true)

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewState | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const previewTimerRef = useRef<number | null>(null)
  const hasInitialPreviewRef = useRef(false)

  const togglePromptType = useCallback((p: PromptTypeKey) => {
    setEnabledPromptTypes((prev) => {
      if (prev.includes(p)) {
        if (prev.length <= 1) return prev
        return prev.filter((x) => x !== p)
      }
      return [...prev, p]
    })
  }, [])

  const toggleSkillType = useCallback((id: string) => {
    setEnabledSkillTypes((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev
        return prev.filter((x) => x !== id)
      }
      return [...prev, id]
    })
  }, [])

  const compatibleAnswerTypes = useMemo(
    () => unionAnswerTypesForPromptPool(enabledPromptTypes as string[], enabledSkillTypes),
    [enabledPromptTypes, enabledSkillTypes],
  )

  const granularEnabledAnswerTypes = useMemo(() => {
    const fromModes = granularAnswerTypesFromModes(enabledAnswerFormatModes, compatibleAnswerTypes)
    if (!autoCheckEnabled) {
      const speechGranular = compatibleAnswerTypes.filter((at) => isSpeechAnswerType(at))
      if (enabledAnswerFormatModes.length === 0) {
        return speechGranular
      }
      return fromModes.filter((at) => isSpeechAnswerType(at))
    }
    return fromModes
  }, [autoCheckEnabled, enabledAnswerFormatModes, compatibleAnswerTypes])

  useEffect(() => {
    setEnabledAnswerFormatModes((prev) =>
      prev.filter((m) => modeHasCompatibleGranular(m, compatibleAnswerTypes)),
    )
  }, [compatibleAnswerTypes])

  useEffect(() => {
    if (!autoCheckEnabled) {
      setEnabledAnswerFormatModes((prev) => prev.filter((m) => m === 'speech'))
    }
  }, [autoCheckEnabled])

  const previewRequestPayload = useMemo((): TestPreviewRunRequestPayload | null => {
    const uid = me?.id
    if (uid == null || !Number.isFinite(Number(uid))) return null
    return {
      target_id: Number(uid),
      target_type: 'user',
      exclude_duplicate_words: false,
      enabled_prompt_type: enabledPromptTypes as string[],
      enabled_answer_type: granularEnabledAnswerTypes,
      auto_check_enabled: autoCheckEnabled,
      filters: {
        relax_filter: true,
        tag_ids: [],
        attribute_conditions: [],
      },
      completion_method: { mode: 'classic' },
      help_settings: {
        allow_hints: allowHints,
        allow_transcription: allowTranscription,
      },
      explicit_card_ids: [],
    }
  }, [
    me?.id,
    enabledPromptTypes,
    granularEnabledAnswerTypes,
    autoCheckEnabled,
    allowHints,
    allowTranscription,
  ])

  const canPreview = true

  const canRunPreview = useMemo(() => {
    if (!canPreview) return false
    if (!previewRequestPayload) return false
    if (enabledSkillTypes.length === 0) return false
    if (enabledPromptTypes.length === 0) return false
    if (compatibleAnswerTypes.length === 0) return false
    if (autoCheckEnabled && enabledAnswerFormatModes.length === 0) return false
    return true
  }, [
    canPreview,
    previewRequestPayload,
    enabledSkillTypes.length,
    enabledPromptTypes.length,
    compatibleAnswerTypes.length,
    autoCheckEnabled,
    enabledAnswerFormatModes.length,
  ])

  const previewSelectionBlockHint = useMemo(() => {
    if (!canPreview || canRunPreview) return null
    return t('tests.createForm.previewNeedDisplayAndFormatHint', {
      display: t('tests.createForm.sectionDisplay'),
      answerFormat: t('tests.createForm.sectionAnswerFormat'),
    })
  }, [canPreview, canRunPreview, t])

  const handlePreview = useCallback(async () => {
    if (!previewRequestPayload) return
    setError(null)
    setIsPreviewLoading(true)
    try {
      const res = await testsApi.preview(previewRequestPayload)
      const u0 = Array.isArray(res.users) ? res.users[0] : null
      const cards = Array.isArray(u0?.cards) ? u0.cards : []
      setPreview({
        count_need: (u0?.count_need ?? 0) as number,
        count_found: (u0?.count_found ?? 0) as number,
        shortage: (u0?.shortage ?? 0) as number,
        card_ids: cards.map((c) => Number(c.id)).filter((x) => Number.isFinite(x)),
      })
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsPreviewLoading(false)
    }
  }, [previewRequestPayload])

  useEffect(() => {
    if (!canRunPreview) return
    if (hasInitialPreviewRef.current) return
    hasInitialPreviewRef.current = true
    void handlePreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview])

  useEffect(() => {
    if (!canRunPreview) return
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = window.setTimeout(() => {
      void handlePreview()
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
  }, [canRunPreview, previewRequestPayload, handlePreview])

  useEffect(() => {
    if (canRunPreview) return
    setPreview(null)
    setIsPreviewLoading(false)
  }, [canRunPreview])

  const handleSubmit = async () => {
    setError(null)
    setIsSubmitting(true)
    try {
      if (!previewRequestPayload) {
        throw new Error('Недостаточно данных для создания теста')
      }
      const res = await testsApi.createSelf(previewRequestPayload)
      navigate(ROUTES.TEST_RUN(res.id))
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsSubmitting(false)
    }
  }

  const displayName = me?.display_name || me?.nickname || me?.email || ''

  return (
    <DashboardLayout>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={ROUTES.DASHBOARD}
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('dashboard.home')}
        </Link>
        <Link component={RouterLink} to={ROUTES.TESTS} underline="hover" color="inherit">
          {t('tests.title')}
        </Link>
        <Typography color="text.primary">{t('tests.selfCreateTitle')}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('tests.selfCreateTitle')}
        </Typography>
        <PageHelpPopover pageKey="tests-self-create" />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 360px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <MuiCard id="display" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<VisibilityIcon fontSize="small" />}
                    title={t('tests.createForm.sectionDisplay')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {t('tests.form.selfTestPromptLimitedHint')}
                  </Typography>
                  <FormGroup>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      {SELF_PROMPT_OPTIONS.map((opt) => {
                        const checked = enabledPromptTypes.includes(opt.id)
                        const inputId = `self-test-prompt-${opt.id}`
                        return (
                          <Paper
                            key={opt.id}
                            variant="outlined"
                            sx={(theme) => ({
                              overflow: 'hidden',
                              borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                              bgcolor: checked
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'light' ? 0.12 : 0.22,
                                  )
                                : theme.palette.background.paper,
                              transition: theme.transitions.create(['background-color', 'border-color'], {
                                duration: theme.transitions.duration.shorter,
                              }),
                            })}
                          >
                            <Box
                              component="label"
                              htmlFor={inputId}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                                p: 1.25,
                                m: 0,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <Checkbox
                                id={inputId}
                                checked={checked}
                                onChange={() => togglePromptType(opt.id)}
                                color="primary"
                                sx={{
                                  p: 0,
                                  mt: 0.125,
                                  color: (theme) =>
                                    checked ? theme.palette.primary.main : theme.palette.action.active,
                                  '&.Mui-checked': {
                                    color: (theme) => theme.palette.primary.main,
                                  },
                                }}
                              />
                              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                                <Typography
                                  variant="body2"
                                  sx={(theme) => ({
                                    fontWeight: 600,
                                    color: checked
                                      ? theme.palette.mode === 'light'
                                        ? theme.palette.primary.dark
                                        : theme.palette.primary.light
                                      : theme.palette.text.primary,
                                  })}
                                >
                                  {t(`tests.run.promptTypes.${opt.id}`)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  component="span"
                                  sx={(theme) => ({
                                    display: 'block',
                                    lineHeight: 1.35,
                                    color: checked
                                      ? theme.palette.mode === 'light'
                                        ? alpha(theme.palette.primary.dark, 0.92)
                                        : alpha(theme.palette.primary.light, 0.9)
                                      : theme.palette.text.secondary,
                                  })}
                                >
                                  {t(`tests.createForm.promptTypeDescriptions.${opt.id}`, { defaultValue: '' })}
                                </Typography>
                              </Stack>
                            </Box>
                          </Paper>
                        )
                      })}
                    </Box>
                  </FormGroup>
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="answer-format" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<ChatBubbleOutlineIcon fontSize="small" />}
                    title={t('tests.createForm.sectionAnswerFormat')}
                  />
                  {compatibleAnswerTypes.length > 0 ? (
                    <FormGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                        {t(
                          autoCheckEnabled
                            ? 'tests.createForm.answerFormatModesHintAutoOn'
                            : 'tests.createForm.answerFormatModesHintAutoOff',
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                          gap: 1,
                        }}
                      >
                        {ANSWER_FORMAT_MODES.map((mode) => {
                          const matrixAllows = modeHasCompatibleGranular(mode, compatibleAnswerTypes)
                          const available = autoCheckEnabled ? matrixAllows : mode === 'speech' && matrixAllows
                          const checked = enabledAnswerFormatModes.includes(mode)
                          const inputId = `self-test-answer-mode-${mode}`
                          const toggleMode = () => {
                            if (!available) return
                            setEnabledAnswerFormatModes((prev) =>
                              prev.includes(mode) ? prev.filter((x) => x !== mode) : [...prev, mode],
                            )
                          }
                          const card = (
                            <Paper
                              variant="outlined"
                              sx={(theme) => ({
                                overflow: 'hidden',
                                borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                                bgcolor: checked
                                  ? alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === 'light' ? 0.12 : 0.22,
                                    )
                                  : theme.palette.background.paper,
                                opacity: available ? 1 : 0.55,
                                transition: theme.transitions.create(['background-color', 'border-color', 'opacity'], {
                                  duration: theme.transitions.duration.shorter,
                                }),
                              })}
                            >
                              <Box
                                component="label"
                                htmlFor={inputId}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1.25,
                                  p: 1.25,
                                  m: 0,
                                  cursor: available ? 'pointer' : 'not-allowed',
                                  userSelect: 'none',
                                }}
                              >
                                <Checkbox
                                  id={inputId}
                                  checked={checked}
                                  disabled={!available}
                                  onChange={toggleMode}
                                  color="primary"
                                  sx={{
                                    p: 0,
                                    mt: 0.125,
                                    color: (theme) =>
                                      checked ? theme.palette.primary.main : theme.palette.action.active,
                                    '&.Mui-checked': {
                                      color: (theme) => theme.palette.primary.main,
                                    },
                                  }}
                                />
                                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                                  <Typography
                                    variant="body2"
                                    sx={(theme) => ({
                                      fontWeight: 600,
                                      color: checked
                                        ? theme.palette.mode === 'light'
                                          ? theme.palette.primary.dark
                                          : theme.palette.primary.light
                                        : theme.palette.text.primary,
                                    })}
                                  >
                                    {t(`tests.createForm.answerFormatModes.${mode}.title`)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="span"
                                    sx={(theme) => ({
                                      display: 'block',
                                      lineHeight: 1.35,
                                      color: checked
                                        ? theme.palette.mode === 'light'
                                          ? alpha(theme.palette.primary.dark, 0.92)
                                          : alpha(theme.palette.primary.light, 0.9)
                                        : theme.palette.text.secondary,
                                    })}
                                  >
                                    {t(`tests.createForm.answerFormatModes.${mode}.description`)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Paper>
                          )
                          const tooltipTitle =
                            !matrixAllows
                              ? t('tests.createForm.answerFormatModeUnavailable')
                              : !autoCheckEnabled && mode !== 'speech'
                                ? t('tests.createForm.answerFormatTypedChooseRequireAutoCheck')
                                : ''
                          return (
                            <Box key={mode} sx={{ width: '100%', minWidth: 0 }}>
                              {available ? (
                                card
                              ) : (
                                <Tooltip title={tooltipTitle} arrow>
                                  <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                    {card}
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          )
                        })}
                      </Box>
                    </FormGroup>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('tests.form.answerFormatUnavailableHint')}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Switch checked={autoCheckEnabled} onChange={(e) => setAutoCheckEnabled(e.target.checked)} />
                    }
                    label={t('tests.form.autoCheckEnabled')}
                  />
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="skills" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<TrackChangesIcon fontSize="small" />}
                    title={t('tests.createForm.sectionCheckGoal')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {t('tests.form.selfTestSkillsRestrictedHint')}
                  </Typography>
                  <FormGroup>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      {SELF_SKILL_IDS.map((skillId) => {
                        const checked = enabledSkillTypes.includes(skillId)
                        const inputId = `self-test-skill-${skillId}`
                        return (
                          <Paper
                            key={skillId}
                            variant="outlined"
                            sx={(theme) => ({
                              overflow: 'hidden',
                              borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                              bgcolor: checked
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'light' ? 0.12 : 0.22,
                                  )
                                : theme.palette.background.paper,
                              transition: theme.transitions.create(['background-color', 'border-color'], {
                                duration: theme.transitions.duration.shorter,
                              }),
                            })}
                          >
                            <Box
                              component="label"
                              htmlFor={inputId}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                                p: 1.25,
                                m: 0,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <Checkbox
                                id={inputId}
                                checked={checked}
                                onChange={() => toggleSkillType(skillId)}
                                color="primary"
                                sx={{
                                  p: 0,
                                  mt: 0.125,
                                  color: (theme) =>
                                    checked ? theme.palette.primary.main : theme.palette.action.active,
                                  '&.Mui-checked': {
                                    color: (theme) => theme.palette.primary.main,
                                  },
                                }}
                              />
                              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                                <Typography
                                  variant="body2"
                                  sx={(theme) => ({
                                    fontWeight: 600,
                                    color: checked
                                      ? theme.palette.mode === 'light'
                                        ? theme.palette.primary.dark
                                        : theme.palette.primary.light
                                      : theme.palette.text.primary,
                                  })}
                                >
                                  {t(`tests.run.skillLabels.${skillId}`, { defaultValue: skillId })}
                                </Typography>
                              </Stack>
                            </Box>
                          </Paper>
                        )
                      })}
                    </Box>
                  </FormGroup>
                </Stack>
              </CardContent>
            </MuiCard>

            <MuiCard id="hints" sx={LESSON_TEST_SECTION_CARD_SX}>
              <CardContent sx={LESSON_TEST_SECTION_CARD_CONTENT_SX}>
                <Stack spacing={2}>
                  <TestCreateSectionHeader
                    icon={<LightbulbIcon fontSize="small" />}
                    title={t('tests.createForm.sectionHints')}
                  />
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                      control={<Switch checked={allowHints} onChange={(e) => setAllowHints(e.target.checked)} />}
                      label={t('tests.form.allowHints')}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allowTranscription}
                          onChange={(e) => setAllowTranscription(e.target.checked)}
                        />
                      }
                      label={t('tests.form.allowTranscription')}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </MuiCard>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Avatar
                src={me?.avatar_url ?? undefined}
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                  color: 'primary.main',
                  fontWeight: 700,
                })}
              >
                {(displayName || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>{t('tests.kind.self', { defaultValue: 'Самостоятельный' })}</strong>
                </Typography>
                {displayName ? (
                  <Typography variant="body2" fontWeight={600} noWrap title={displayName}>
                    {displayName}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            fullWidth
            sx={{ textTransform: 'none', height: 40 }}
            disabled={!canRunPreview || isSubmitting}
          >
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>{t('common.saving')}</span>
              </Stack>
            ) : (
              t('tests.actions.create')
            )}
          </Button>
          <SelfPreviewPanel
            preview={preview}
            isLoading={isPreviewLoading}
            selectionBlockHint={previewSelectionBlockHint}
            canRunPreview={canRunPreview}
          />
        </Stack>
      </Box>
    </DashboardLayout>
  )
}
